"use client";

import { useState } from "react";
import Button, { ButtonSize, ButtonVariant } from "@/app/(components)/generics/Button/Button";
import IconInfo from "@/app/(components)/generics/Icons/IconInfo";
import ChoiceInput from "@/app/(components)/generics/inputs/default/ChoiceInput/ChoiceInput";
import { Input } from "@/app/(components)/generics/inputs/default/Input/Input";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import SuccessMessage from "@/app/(components)/generics/messages/SuccessMessage/SuccessMessage";
import { savePractitionerProfile } from "@/app/actions/practitioner/action";
import { UserStore } from "@/app/stores/UserStore";
import { TKey } from "@/app/translations/TKey";
import { T, TErrors } from "@/app/translations/Translations";
import { getProfessions, PROFESSIONAL_ID_TYPES } from "@/app/vars/PractitionerVars";
import styles from "./tabs.module.css";

type Props = { lang: string };

export default function Practitioner({ lang }: Props) {
  const user = UserStore((state) => state.user);
  const setUser = UserStore((state) => state.setUser);

  const profile = user?.practitionerProfile;

  const professions = getProfessions(lang);

  const isOther = profile?.profession
    ? !professions.some((p) => p.text === profile.profession)
    : false;

  const [data, setData] = useState({
    profession: isOther ? "" : (profile?.profession ?? ""),
    professionOther: isOther ? (profile?.profession ?? "") : "",
    professionalIdType: profile?.professionalIdType ?? "",
    professionalIdNumber: profile?.professionalIdNumber ?? "",
  });

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    profession: "",
    professionalIdType: "",
    professionalIdNumber: "",
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setFieldErrors({ profession: "", professionalIdType: "", professionalIdNumber: "" });
    setSaving(true);

    const profession = data.profession || data.professionOther;

    const result = await savePractitionerProfile({
      profession,
      professionalIdType: data.professionalIdType,
      professionalIdNumber: data.professionalIdNumber,
    });

    if (result.success) {
      setSuccess(true);
      setUser({
        ...user!,
        practitionerProfile: {
          profession,
          professionalIdType: data.professionalIdType,
          professionalIdNumber: data.professionalIdNumber,
        },
      });
    } else if (result.fieldErrors) {
      setFieldErrors(TErrors(result.fieldErrors, lang));
    } else {
      setError(T(TKey.ERROR, lang) as string);
    }

    setSaving(false);
  };

  const practice = profile?.practice;

  return (
    <div className={styles.container}>
      <form className={styles.form} onSubmit={handleSave}>
        <ChoiceInput
          label={T(TKey.SETTINGS_PROFESSION, lang) as string}
          lang={lang}
          isRequired
          showRequiredLabel
          data={data}
          dataKey="profession"
          options={professions}
          otherOption={{
            dataKey: "professionOther",
            text: T(TKey.SETTINGS_PROFESSION_OTHER_LABEL, lang) as string,
            input: {
              data,
              dataKey: "professionOther",
              placeholder: T(TKey.SETTINGS_PROFESSION_OTHER_PLACEHOLDER, lang) as string,
              onChange: (d) => setData({ ...d }),
            },
          }}
          required={{ visible: !!fieldErrors.profession, text: fieldErrors.profession }}
        />

        <ChoiceInput
          label={T(TKey.SETTINGS_PROFESSIONAL_ID_TYPE, lang) as string}
          lang={lang}
          isRequired
          showRequiredLabel
          data={data}
          dataKey="professionalIdType"
          options={PROFESSIONAL_ID_TYPES}
          required={{
            visible: !!fieldErrors.professionalIdType,
            text: fieldErrors.professionalIdType,
          }}
        />

        <Input
          data={data}
          dataKey="professionalIdNumber"
          label={T(TKey.SETTINGS_PROFESSIONAL_ID_NUMBER, lang) as string}
          lang={lang}
          required
          showRequiredLabel
          placeholder={T(TKey.SETTINGS_PROFESSIONAL_ID_NUMBER, lang) as string}
          onChange={(d) => setData({ ...d })}
          errorMessage={fieldErrors.professionalIdNumber}
        />

        {error && <ErrorMessage message={error} />}
        {success && (
          <SuccessMessage message={T(TKey.SAVE, lang) as string} onHide={() => setSuccess(false)} />
        )}

        <div className={styles.actions}>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            type="submit"
            loading={saving}
          >
            {T(TKey.SAVE, lang)}
          </Button>
        </div>
      </form>

      <div className={styles.separator} />

      <div className={styles.practiceSection}>
        <span className={styles.practiceSectionTitle}>{T(TKey.SETTINGS_PRACTICE, lang)}</span>
        {practice ? (
          <>
            <div className={styles.practiceInfo}>
              <span className={styles.practiceName}>{practice.name}</span>
              {practice.address && (
                <span className={styles.practiceDetail}>{practice.address}</span>
              )}
              <span className={styles.practiceDetail}>
                {practice.city}
                {practice.postalCode ? ` ${practice.postalCode}` : ""} · {practice.country}
              </span>
            </div>

            <div className={styles.practiceInfoHint}>
              <IconInfo />
              <span>{T(TKey.SETTINGS_PRACTICE_READONLY, lang)}</span>
            </div>
          </>
        ) : (
          <div className={styles.noPractice}>
            <IconInfo />
            <span>{T(TKey.SETTINGS_NO_PRACTICE, lang)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
